// ============ BaZi — the two things the pillars could not show ============
//
// The four-pillar panel already reads deep. What it could not do is show the
// two structures every BaZi reader draws on paper:
//
//   五行生剋 — the five elements as a ring, with the generating cycle running
//              round the outside and the controlling cycle cutting across the
//              middle. The reader's own weights sit ON the diagram, so "水 9.5%"
//              stops being a number in a list and becomes a thin node that
//              cannot feed the wood next to it.
//   六十甲子 — the sexagenary cycle as its six decades of ten, with the natal
//              pillars, the ten-year luck pillars and this year's pillar all
//              marked in one place. This is the BaZi counterpart of the 64-
//              hexagram chart on the I Ching tab, and it answers the question
//              a list of pillars cannot: where in the cycle does my life sit.
//
// Both are drawn from data the PA JE stack already ships (ELEMENT_CYCLES and
// SIXTY_JIAZI); nothing here is a new claim about the chart.

import React, { useMemo, useState } from 'react';
import { useT, useLang } from '../lib/i18n';

const ELEMENT_C = ['#4ade80', '#ff5c4d', '#f0c75a', '#cbd5e1', '#38bdf8']; // wood fire earth metal water

// ------------------------------------------------------- 五行 生剋 wheel

/**
 * The five elements sit on a pentagon in generating order (木→火→土→金→水→木),
 * which is what makes the two cycles legible at once: 生 is the ring, 剋 is
 * the five-pointed star inside it. Node radius carries the reader's weight.
 */
function ElementWheel({ percent, dmElement, favorable, unfavorable, en }: {
  percent: number[]; dmElement: number; favorable: number[]; unfavorable: number[]; en: boolean;
}) {
  const NAMES = [['木', 'ไม้', 'Wood'], ['火', 'ไฟ', 'Fire'], ['土', 'ดิน', 'Earth'], ['金', 'ทอง', 'Metal'], ['水', 'น้ำ', 'Water']];
  const R = 92, CX = 130, CY = 122;
  // Start at the top and walk clockwise in generating order.
  const pos = NAMES.map((_, i) => {
    const ang = (-90 + i * 72) * (Math.PI / 180);
    return [CX + R * Math.cos(ang), CY + R * Math.sin(ang)] as [number, number];
  });
  const max = Math.max(...percent, 1);
  const rOf = (i: number) => 13 + (percent[i] / max) * 13;

  return (
    <svg viewBox="0 0 260 250" className="mm-bzw" role="img" aria-label={en ? 'Five element cycles' : 'วงจรห้าธาตุ'}>
      <defs>
        <marker id="mmArrGen" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M0,0 L7,3.5 L0,7 z" fill="#4ade80" opacity="0.75"></path>
        </marker>
        <marker id="mmArrCtl" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M0,0 L7,3.5 L0,7 z" fill="#ff5c4d" opacity="0.7"></path>
        </marker>
      </defs>

      {/* 剋 — each element controls the one two steps along, so the chords form
          a star. Drawn first so the generating ring sits on top of it. */}
      {pos.map((p, i) => {
        const q = pos[(i + 2) % 5];
        const shrink = (a: [number, number], b: [number, number], by: number) => {
          const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
          return [a[0] + (dx / L) * by, a[1] + (dy / L) * by] as [number, number];
        };
        const s = shrink(p, q, rOf(i) + 3), e = shrink(q, p, rOf((i + 2) % 5) + 7);
        return <line key={`c${i}`} x1={s[0]} y1={s[1]} x2={e[0]} y2={e[1]} stroke="#ff5c4d" strokeOpacity="0.34" strokeWidth="1.2" strokeDasharray="4 3" markerEnd="url(#mmArrCtl)"></line>;
      })}

      {/* 生 — the ring, drawn as arcs so it reads as a cycle, not a polygon. */}
      {pos.map((p, i) => {
        const q = pos[(i + 1) % 5];
        const shrink = (a: [number, number], b: [number, number], by: number) => {
          const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
          return [a[0] + (dx / L) * by, a[1] + (dy / L) * by] as [number, number];
        };
        const s = shrink(p, q, rOf(i) + 3), e = shrink(q, p, rOf((i + 1) % 5) + 7);
        return <path key={`g${i}`} d={`M${s[0]},${s[1]} A ${R * 1.25},${R * 1.25} 0 0 1 ${e[0]},${e[1]}`}
          fill="none" stroke="#4ade80" strokeOpacity="0.5" strokeWidth="1.6" markerEnd="url(#mmArrGen)"></path>;
      })}

      {pos.map((p, i) => {
        const fav = favorable.includes(i), unfav = unfavorable.includes(i);
        return (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r={rOf(i)} fill={ELEMENT_C[i]} fillOpacity={unfav ? 0.13 : 0.22}
              stroke={ELEMENT_C[i]} strokeOpacity={fav ? 0.95 : 0.4} strokeWidth={fav ? 2.2 : 1.2}></circle>
            {i === dmElement && <circle cx={p[0]} cy={p[1]} r={rOf(i) + 4.5} fill="none" stroke={ELEMENT_C[i]} strokeOpacity="0.55" strokeDasharray="2 3"></circle>}
            <text x={p[0]} y={p[1] + 1} textAnchor="middle" dominantBaseline="middle" fontSize="13" fill={ELEMENT_C[i]} fontWeight="600">{NAMES[i][0]}</text>
            <text x={p[0]} y={p[1] + rOf(i) + 13} textAnchor="middle" fontSize="9" fill="#8a93a8">
              {en ? NAMES[i][2] : NAMES[i][1]} {percent[i].toFixed(1)}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------- 六十甲子

interface JiaziCell { index: number; name: string; stem: number; branch: number; nayin: any; xunHead: string; voidBranchesChinese: string[]; }

export function BaziExtras({ a, SIXTY_JIAZI }: { a: any; SIXTY_JIAZI: JiaziCell[] }) {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const [open, setOpen] = useState<number | null>(null);

  /** Where each of the reader's own pillars sits in the sixty. */
  const marks = useMemo(() => {
    const m = new Map<number, string[]>();
    const put = (stem: number, branch: number, tag: string) => {
      const idx = SIXTY_JIAZI.findIndex(j => j.stem === stem && j.branch === branch);
      if (idx < 0) return;
      m.set(idx, [...(m.get(idx) || []), tag]);
    };
    for (const p of a.pillars) put(p.stem.index, p.branch.index, p.zh);
    for (const l of a.luck.list) put(l.pillar.stem, l.pillar.branch, `${t('เสาโชค', 'luck')} ${l.ageFrom}`);
    if (a.currentYear) put(a.currentYear.pillar.stem, a.currentYear.pillar.branch, t('ปีนี้', 'this year'));
    return m;
  }, [a, SIXTY_JIAZI, t]);

  const natal = useMemo(() => new Set(a.pillars.map((p: any) =>
    SIXTY_JIAZI.findIndex(j => j.stem === p.stem.index && j.branch === p.branch.index))), [a, SIXTY_JIAZI]);
  const luck = useMemo(() => new Set(a.luck.list.map((l: any) =>
    SIXTY_JIAZI.findIndex(j => j.stem === l.pillar.stem && j.branch === l.pillar.branch))), [a, SIXTY_JIAZI]);
  const yearIdx = a.currentYear
    ? SIXTY_JIAZI.findIndex(j => j.stem === a.currentYear.pillar.stem && j.branch === a.currentYear.pillar.branch)
    : -1;
  const myXun = a.xun?.xun;

  const rows = [0, 1, 2, 3, 4, 5];

  return (
    <React.Fragment>
      <div className="mm-bz-wheelwrap">
        <ElementWheel percent={a.scores.percent} dmElement={a.strength.dmElement}
          favorable={a.usefulGod?.favorable || []} unfavorable={a.usefulGod?.unfavorable || []} en={en}></ElementWheel>
        <div className="mm-bz-wlegend">
          <p><i className="gen"></i>{t('วงนอก 相生 — ธาตุก่อเกิดกันตามเข็ม', 'Outer ring 相生 — each element generates the next')}</p>
          <p><i className="ctl"></i>{t('เส้นประกลาง 相剋 — ธาตุข่มกันข้ามวง', 'Inner star 相剋 — each element controls the one two steps on')}</p>
          <p><i className="dm"></i>{t('วงประรอบธาตุเจ้าชะตา', 'Dashed ring marks the Day Master element')}</p>
          <p><i className="fav"></i>{t('เส้นขอบหนา = 用神 ธาตุที่เป็นคุณ · ธาตุจาง = 忌神 ที่ควรลด', 'Thick outline = 用神 favourable · faded = 忌神 to reduce')}</p>
          <p className="dim">{t(
            'ขนาดวงกลมคือน้ำหนักธาตุจริงในดวงคุณ — ธาตุที่เล็กมากคือจุดที่วงจรขาด ไม่ใช่แค่ตัวเลขน้อย',
            'Circle size is the real weight in your chart — a tiny node is where the cycle breaks, not merely a small number.')}</p>
        </div>
      </div>

      <div className="mm-bz-60">
        <div className="mm-bz-60head">
          <b className="mono dim">{t('六十甲子 · วัฏจักรหกสิบ', '六十甲子 · the sexagenary cycle')}</b>
          <span className="mm-bz-key"><i className="natal"></i>{t('เสาในดวง', 'natal')}</span>
          <span className="mm-bz-key"><i className="luck"></i>{t('เสาโชค', 'luck')}</span>
          <span className="mm-bz-key"><i className="year"></i>{t('ปีไหลเวียน', 'this year')}</span>
        </div>
        <div className="mm-bz-60scroll">
          <table className="mm-bz-60t">
            <tbody>
              {rows.map(r => (
                <tr key={r} className={myXun === r ? 'myxun' : ''}>
                  <th className="mono dim">{SIXTY_JIAZI[r * 10].xunHead}<small>{t('รอบ', 'xun')}</small></th>
                  {Array.from({ length: 10 }, (_, c) => {
                    const idx = r * 10 + c;
                    const j = SIXTY_JIAZI[idx];
                    const cls = [
                      natal.has(idx) ? 'natal' : '',
                      luck.has(idx) ? 'luck' : '',
                      idx === yearIdx ? 'year' : '',
                      open === idx ? 'on' : '',
                    ].filter(Boolean).join(' ');
                    return (
                      <td key={c}>
                        <button className={`mm-bz-cell ${cls}`} onClick={() => setOpen(open === idx ? null : idx)}
                          title={`${j.name} · ${j.nayin?.chinese || ''}`}>
                          {j.name}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {open != null && (() => {
          const j = SIXTY_JIAZI[open];
          const tags = marks.get(open) || [];
          return (
            <div className="mm-bz-60open">
              <b>{j.name} <em className="dim">{(j as any).pinyin}</em></b>
              <span>納音 {j.nayin?.chinese} · {en ? j.nayin?.en : (j.nayin as any)?.th}</span>
              <span className="dim">{t('อยู่รอบ', 'in the xun of')} {j.xunHead} · {t('ว่าง 空亡', 'void 空亡')} {j.voidBranchesChinese?.join(' ')}</span>
              {tags.length > 0 && <span className="mm-bz-60mine">{t('อยู่ในดวงคุณ', 'in your chart')}: {tags.join(' · ')}</span>}
            </div>
          );
        })()}
        <p className="mm-footnote dim">{t(
          'หกแถวคือหก "รอบสิบ" (旬) — แถวที่เรืองแสงคือรอบของเสาวันคุณ ซึ่งเป็นตัวกำหนดว่าราศีใดเป็น 空亡 · กดที่ช่องเพื่อดู 納音 และรอบของมัน',
          'Six rows are the six decades (旬) — the glowing row is your day pillar\'s, which is what decides your 空亡 branches. Tap a cell for its 納音 and decade.')}</p>
      </div>
    </React.Fragment>
  );
}
